import React, { useState } from 'react';
import './Login.css';
import { requestOTP } from '../services/authService';

const Login = ({ onOTPSent, onSignupClick }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+92');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) setPhoneNumber(value);
    if (error) setError('');
  };

  const handleCountryCodeChange = (e) => {
    setCountryCode(e.target.value);
    if (error) setError('');
  };

  const validatePhoneNumber = () => {
    if (!phoneNumber.trim()) {
      setError('Please enter your phone number');
      return false;
    }
    if (phoneNumber.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    return true;
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (!validatePhoneNumber()) return;

    setLoading(true);
    try {
      const fullPhoneNumber = `${countryCode}${phoneNumber}`;
      const data = await requestOTP(fullPhoneNumber);
      setSuccessMessage(data.message || 'OTP sent successfully! Check your phone.');
      setPhoneNumber('');
      setTimeout(() => { onOTPSent(fullPhoneNumber); }, 1200);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">

      {/* ── Left Hero Panel ── */}
      <aside className="login-hero" aria-hidden="true">
        <div className="hero-ring hero-ring-1" />
        <div className="hero-ring hero-ring-2" />
        <div className="login-hero-content">
          <div className="hero-logo-wrap">⚖️</div>
          <h2 className="hero-title">
            Pakistan's Premier<br /><em>Legal Platform</em>
          </h2>
          <p className="hero-sub">
            Connect with top-tier lawyers, manage your cases,
            and access justice — all from one secure platform.
          </p>
          <div className="hero-pills">
            <div className="hero-pill">
              <span className="hero-pill-icon">🔒</span>
              <span>Bank-Grade Security</span>
            </div>
            <div className="hero-pill">
              <span className="hero-pill-icon">⚡</span>
              <span>Instant OTP Login</span>
            </div>
            <div className="hero-pill">
              <span className="hero-pill-icon">🏛️</span>
              <span>Verified Lawyers</span>
            </div>
          </div>
        </div>
      </aside>

      {/* ── Right Form Panel ── */}
      <main className="login-form-panel">
        <div className="login-card">

          {/* Header */}
          <div className="login-card-eyebrow">
            <span className="eyebrow-dot" />
            <span className="eyebrow-text">INSAFAPP.COM</span>
          </div>
          <h1 className="login-card-title">Welcome Back</h1>
          <p className="login-card-desc">
            Enter your phone number to receive a one-time password.
          </p>
          <div className="login-card-divider" />

          {/* Form */}
          <form onSubmit={handleRequestOTP} noValidate>
            <div className="login-field">
              <label htmlFor="phone-number" className="login-field-label">
                Phone Number
              </label>
              <div className={`phone-input-container${error ? ' has-error' : ''}`}>
                <div className="country-code-wrapper">
                  <select
                    value={countryCode}
                    onChange={handleCountryCodeChange}
                    className="country-code-select"
                    disabled={loading}
                    aria-label="Country code"
                  >
                    <option value="+92">🇵🇰 +92</option>
                  </select>
                </div>
                <input
                  id="phone-number"
                  type="tel"
                  inputMode="numeric"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="3XX XXXXXXX"
                  className={`phone-input${error ? ' error' : ''}`}
                  disabled={loading}
                  autoComplete="tel-national"
                  aria-describedby={error ? 'phone-error' : undefined}
                />
                <span className="character-count" aria-live="polite">
                  {phoneNumber.length}/10
                </span>
              </div>

              {error && (
                <div id="phone-error" className="login-status error" role="alert">
                  <span className="status-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
              {successMessage && (
                <div className="login-status success" role="status">
                  <span className="status-icon">✅</span>
                  <span>{successMessage}</span>
                </div>
              )}
            </div>

            <button
              type="submit"
              className={`otp-button${loading ? ' loading' : ''}`}
              disabled={loading}
            >
              {loading ? 'Sending OTP…' : 'Request OTP →'}
            </button>
          </form>

          {/* Footer */}
          <div className="login-card-footer">
            <p>
              Don't have an account?{' '}
              <a
                href="#signup"
                onClick={(e) => { e.preventDefault(); onSignupClick(); }}
              >
                Create one free
              </a>
            </p>
          </div>

          {/* Trust strip */}
          <div className="login-trust-strip">
            <div className="trust-item">
              <span>🔐</span>
              <span>Encrypted</span>
            </div>
            <div className="trust-item">
              <span>🛡️</span>
              <span>Private &amp; Secure</span>
            </div>
            <div className="trust-item">
              <span>✅</span>
              <span>Verified Platform</span>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Login;
